import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Send, ArrowLeft, MessageCircle } from 'lucide-react';
import { getApiUrl } from '../config/config';
import { useAuth } from '../context/AuthContext'; 

// ==========================================
// STYLED COMPONENTS (Ostaju iste)
// ==========================================
const PageContainer = styled.div`min-height: calc(100vh - 72px); background: #000; display: flex; align-items: center; justify-content: center; padding: 20px; @media(max-width:768px){padding:0;min-height:calc(100dvh - 64px);}`;
const ChatAppWrapper = styled.div`width: 100%; max-width: 1200px; height: min(85vh,850px); background: #111; border: 1px solid #333; border-radius: 16px; display: flex; overflow: hidden; box-shadow: 0 10px 40px rgba(0,0,0,0.5); @media(max-width:768px){height:calc(100dvh - 64px);border:0;border-radius:0;}`;
const Sidebar = styled.div`width: 320px; background: #0a0a0a; border-right: 1px solid #333; display: flex; flex-direction: column; @media (max-width: 768px) { display: ${props => props.activeChat ? 'none' : 'flex'}; width: 100%; }`;
const SidebarHeader = styled.div`padding: 24px 20px; border-bottom: 1px solid #333; color: #fff; font-family: var(--font-heading); font-size: 1.3rem; display: flex; align-items: center; gap: 12px; svg { cursor: pointer; color: #e0c16f; transition: transform 0.2s; } svg:hover { transform: translateX(-3px); }`;
const ContactList = styled.div`flex: 1; overflow-y: auto; padding: 12px; &::-webkit-scrollbar { width: 4px; } &::-webkit-scrollbar-thumb { background: #333; border-radius: 2px; }`;
const ContactItem = styled.div`display: flex; align-items: center; gap: 14px; padding: 12px; border-radius: 10px; background: ${props => props.active ? 'linear-gradient(135deg, #1a1a1a 0%, #222 100%)' : 'transparent'}; border: 1px solid ${props => props.active ? '#333' : 'transparent'}; cursor: pointer; margin-bottom: 4px; transition: all 0.2s; &:hover { background: #1a1a1a; }`;
const ContactAvatar = styled.div`width: 45px; height: 45px; border-radius: 50%; background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); display: flex; align-items: center; justify-content: center; color: #000; font-weight: bold; flex-shrink: 0;`;
const ContactInfo = styled.div`flex: 1; overflow: hidden;`;
const ContactName = styled.div`color: #fff; font-family: var(--font-heading); font-size: 1rem; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const ContactSub = styled.div`color: #888; font-family: var(--font-body); font-size: 0.8rem; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const ChatArea = styled.div`flex: 1; display: flex; flex-direction: column; background: #111; @media (max-width: 768px) { display: ${props => props.activeChat ? 'flex' : 'none'}; }`;
const ChatHeader = styled.div`padding: 20px 24px; border-bottom: 1px solid #333; display: flex; align-items: center; gap: 16px; color: #fff; background: #151515;`;
const MessageList = styled.div`flex: 1; padding: 24px; overflow-y: auto; display: flex; flex-direction: column; gap: 16px; &::-webkit-scrollbar { width: 6px; } &::-webkit-scrollbar-thumb { background: #333; border-radius: 3px; }`;
const MessageBubble = styled.div`max-width: min(70%,620px); padding: 14px 20px; border-radius: 18px; font-family: var(--font-body); font-size: 0.95rem; line-height: 1.5; overflow-wrap:anywhere; box-shadow: 0 4px 15px rgba(0,0,0,0.2); background: ${props => props.isMine ? 'linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%)' : '#222'}; color: ${props => props.isMine ? '#000' : '#fff'}; align-self: ${props => props.isMine ? 'flex-end' : 'flex-start'}; border-bottom-right-radius: ${props => props.isMine ? '4px' : '18px'}; border-bottom-left-radius: ${props => props.isMine ? '18px' : '4px'}; @media(max-width:600px){max-width:88%;padding:11px 14px;}`;
const InputArea = styled.div`padding: 24px; border-top: 1px solid #333; display: flex; gap: 16px; background: #151515; @media(max-width:600px){padding:12px;gap:8px;}`;
const ChatInput = styled.input`flex: 1; background: #000; border: 1px solid #333; padding: 16px 20px; color: #fff; border-radius: 12px; font-family: var(--font-body); font-size: 1rem; transition: all 0.3s; &:focus { outline: none; border-color: #e0c16f; box-shadow: 0 0 10px rgba(224, 193, 111, 0.1); }`;
const SendButton = styled.button`background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); border: none; width: 56px; border-radius: 12px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; &:hover { transform: translateY(-2px); box-shadow: 0 5px 15px rgba(224, 193, 111, 0.3); } &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none; }`;

const Messages = () => {
  const { userId } = useParams(); 
  const navigate = useNavigate();
  const location = useLocation(); // Hvatamo prosleđene podatke
  const scrollRef = useRef();
  
  const { user, isAuthenticated } = useAuth(); 
  
  const [myId, setMyId] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Funkcija koja osvežava listu korisnika (Inbox)
  const fetchInbox = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await fetch(`${getApiUrl()}/messages/inbox/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!isAuthenticated() || !user) {
      navigate('/login');
      return;
    }
    setMyId(user.id);
    fetchInbox(); // Učitaj inbox čim se stranica otvori
  }, [navigate, isAuthenticated, user]);

  useEffect(() => {
    if (!myId || !userId || userId === 'list') return;

    setLoading(true);

    const loadConversation = () => fetch(`${getApiUrl()}/messages/${userId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      }).then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Unable to load conversation');
        if (Array.isArray(data)) setMessages(data);
        setError('');
      }).catch(err => setError(err.message)).finally(() => setLoading(false));
    loadConversation();
    const poll = window.setInterval(loadConversation, 5000);

    return () => window.clearInterval(poll);
  }, [myId, userId]);

  useEffect(() => { scrollRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async () => {
    if (!text.trim() || userId === 'list') return;
    const newMessage = { sender_id: myId, receiver_id: parseInt(userId), message: text, created_at: new Date().toISOString() };
    
    try {
      const response = await fetch(`${getApiUrl()}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ receiverId: userId, message: newMessage.message })
      });
      const saved = await response.json();
      if (!response.ok) throw new Error(saved.error || 'Unable to send message');
      setMessages(prev => [...prev, saved]);
      setText('');
      setError('');
      fetchInbox();
    } catch(err) {
        setError(err.message);
    }
  };

  // Logika za prikaz imena
  const activeContact = contacts.find(c => c.id === parseInt(userId));
  // Ako ga nema u Inboxu, koristi ime prosleđeno iz profila (ili "User #ID" kao zadnju opciju)
  const displayChatName = activeContact 
    ? (activeContact.display_name || activeContact.username) 
    : (location.state?.username || `User #${userId}`);
    
  const displayAvatarLetter = activeContact
    ? (activeContact.display_name || activeContact.username || '?')[0].toUpperCase()
    : (location.state?.username ? location.state.username[0].toUpperCase() : '?');

  return (
    <PageContainer>
      <ChatAppWrapper>
        
        {/* SIDEBAR */}
        <Sidebar activeChat={userId !== 'list'}>
          <SidebarHeader>
            <ArrowLeft onClick={() => navigate('/')} size={24} /> Inbox
          </SidebarHeader>
          <ContactList>
            {contacts.length === 0 ? <p style={{color:'#444', textAlign:'center', marginTop:'20px'}}>No chats yet</p> : 
              contacts.map(c => (
                <ContactItem key={c.id} active={parseInt(userId) === c.id} onClick={() => navigate(`/messages/${c.id}`)}>
                  <ContactAvatar>{(c.display_name || c.username || '?')[0].toUpperCase()}</ContactAvatar>
                  <ContactInfo>
                    <ContactName>{c.display_name || c.username}</ContactName>
                    <ContactSub>{c.last_message}</ContactSub>
                  </ContactInfo>
                </ContactItem>
              ))
            }
          </ContactList>
        </Sidebar>

        {/* CHAT AREA */}
        <ChatArea activeChat={userId !== 'list'}>
          {userId === 'list' ? (
            <div style={{flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'#444'}}>
              <MessageCircle size={60} style={{marginBottom: '20px'}}/> 
              <h3 style={{fontFamily: 'var(--font-heading)'}}>Select a chat to start messaging</h3>
            </div>
          ) : (
            <>
              <ChatHeader>
                <ArrowLeft className="mobile-only" onClick={() => navigate('/messages/list')} style={{cursor:'pointer', marginRight: '10px'}} />
                <ContactAvatar style={{width:'40px', height:'40px'}}>
                  {displayAvatarLetter}
                </ContactAvatar>
                <div style={{fontWeight:'bold'}}>
                  {displayChatName}
                </div>
              </ChatHeader>
              
              <MessageList>
                {error && <div style={{color:'#ff8b96', textAlign:'center'}}>{error}</div>}
                {loading ? <div style={{textAlign:'center', color:'#666'}}>Loading...</div> : 
                  messages.map((m, i) => <MessageBubble key={i} isMine={parseInt(m.sender_id) === parseInt(myId)}>{m.message}</MessageBubble>)
                }
                <div ref={scrollRef} />
              </MessageList>
              
              <InputArea>
                <ChatInput value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message..." onKeyDown={(e) => e.key === 'Enter' && sendMessage()} />
                <SendButton onClick={sendMessage} disabled={!text.trim()}><Send size={20} /></SendButton>
              </InputArea>
            </>
          )}
        </ChatArea>

      </ChatAppWrapper>
    </PageContainer>
  );
};

export default Messages;
